import { useState } from 'react';
import { MAX_WARMUPS, WARMUP_BANK } from '../../../shared/warmupBank';

/** Plain alternatives; changes go through the editor's normal autosave path. */
export default function WarmupPicker({ texts, onChange }: {
  texts: readonly string[];
  onChange: (texts: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState('add');
  const groups = [...new Set(WARMUP_BANK.map(question => question.group))];
  const full = texts.length >= MAX_WARMUPS;
  const replacing = target !== 'add' && Number(target) < texts.length;

  function choose(text: string) {
    if (texts.includes(text)) return;
    if (replacing) onChange(texts.map((current, index) => index === Number(target) ? text : current));
    else if (!full) onChange([...texts, text]);
  }

  return (
    <section aria-label="Warm-up questions" className="mt-4 px-2 py-3 border-t border-line">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="font-mono text-[11px] uppercase tracking-[0.12em] text-faint">Warm-up</h3>
        <span className="text-[11px] text-muted">{texts.length} / {MAX_WARMUPS}</span>
      </div>
      <ul>
        {texts.map((text, index) => (
          <li key={`${index}:${text}`} className="flex items-start gap-2 py-2 text-[13px]">
            <span className="flex-1">{text}</span>
            <button type="button" aria-label={`Remove warm-up ${index + 1}`} onClick={() => {
              onChange(texts.filter((_, i) => i !== index));
              setTarget('add');
            }} className="shrink-0 px-2 py-1 text-faint hover:text-fg">×</button>
          </li>
        ))}
      </ul>
      <button type="button" aria-expanded={open} onClick={() => setOpen(!open)}
        className="mt-2 w-full px-3 py-2 border border-line rounded-sm text-[12px] text-accent hover:bg-elevated">
        {open ? 'Close bank' : 'Add from bank'}
      </button>
      {open && (
        <div className="mt-3">
          <label className="block text-[12px] text-muted mb-2">
            Add or replace
            <select aria-label="Add or replace warm-up" value={replacing ? target : 'add'} onChange={e => setTarget(e.target.value)}
              className="w-full mt-1 px-2 py-2 bg-card border border-line rounded-sm text-fg">
              <option value="add">Add a warm-up</option>
              {texts.map((_, index) => <option key={index} value={String(index)}>Replace W{index + 1}</option>)}
            </select>
          </label>
          {full && !replacing && <p className="text-[12px] text-muted mb-2">Three maximum. Remove or replace one to choose another.</p>}
          {groups.map(group => (
            <details key={group} className="border-t border-line py-2" open>
              <summary className="cursor-pointer text-[12px] text-muted">{group}</summary>
              <ul className="mt-1">
                {WARMUP_BANK.filter(question => question.group === group).map(question => (
                  <li key={question.id}>
                    <button type="button" disabled={texts.includes(question.text) || (full && !replacing)} onClick={() => choose(question.text)}
                      className="w-full text-left text-[13px] px-2 py-2 rounded-sm hover:bg-elevated disabled:opacity-40 disabled:cursor-default">
                      {question.text}
                    </button>
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      )}
    </section>
  );
}
